import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Spin,
  Select,
  Typography,
  Alert,
  message,
  Modal,
  Input,
  Splitter,
} from "antd";
import _ from "lodash";
import { preprocessInvoice } from "./components/preprocessInvoice";
import BuilderTools from "./components/BuilderTools";
import {
  FileAddOutlined,
  DeleteOutlined,
  PushpinOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { SettingOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import ActionsMenu from "../atoms/ActionsMenu";
import CenteredDisplay from "../atoms/CenteredContent";
import {
  loadConfigs,
  createNewConfig,
  loadConfig,
  saveConfig,
  downloadSample,
  deleteConfig,
  setAsDefault,
} from "../../interfaces/invoiceBuilderInterface";
import { safeParse, validateObjectValues } from "../../helpers/helper";
import { useDispatch, useSelector } from "react-redux";
import { setDefaultInvoiceConfig } from "../../features/workspaceSlice";
import TemplatePreview from "./components/PreviewTemplate";

const InvoiceBuilder = () => {
  const [previewData, setPreviewData] = useState({});
  const dispatch = useDispatch()
  const defaultCurrency = useSelector(
    (state) => state.workspace.constants.DEFAULT_CURRENCY,
  );
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState("");
  const [loadedConfig, setLoadedConfig] = useState({}); // for BuilderTools initial values
  const [configCopy, setConfigCopy] = useState({}); // “server” copy for diff/cancel
  const [configName, setConfigName] = useState("");
  const [baseName, setBaseName] = useState(""); // name from server (for diff/cancel)
  const [currency, setCurrency] = useState("$");
  const [baseCurrency, setBaseCurrency] = useState("$"); // currency from server (for diff/cancel)
  const [alert, setAlert] = useState("");
  const [unsaved, setUnsaved] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    companyName: "",
    companyAddress: "",
    serviceAddress: "",
    duePeriod: 30,
    currency:""
  });
  const [formError, setFormError] = useState("");

  // 1) Load list of configs on mount

  const fetchConfigsData = async (hardReload=false) => {
    setLoader(true);
    try {
      const data = await loadConfigs(dispatch);
      setAvailableConfigs(data);

      if (hardReload || (data.length && !selectedConfig)) setSelectedConfig(data[0].id);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchConfigsData();
  }, []);

  // 2) Load a selected config
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedConfig) return;
      setLoader(true);
      try {
        const data = await loadConfig(selectedConfig);
        const parsed = safeParse(data.config);

        setConfigCopy(parsed);
        setLoadedConfig(parsed);
        setPreviewData(parsed);

        setConfigName(data.configName);
        setBaseName(data.configName);

        setCurrency(data.currency);
        setBaseCurrency(data.currency);

        setUnsaved(false);
      } finally {
        setLoader(false);
      }
    };
    fetchData();
  }, [selectedConfig]);

  // 3) Debounced unsaved calculation
  useEffect(() => {
    if (!previewData || !configCopy) return;
    const id = setTimeout(() => {
      const contentChanged = !_.isEqual(previewData, configCopy);
      const nameChanged = configName !== baseName;
      const currencyChanged = currency !== baseCurrency;
      setUnsaved(contentChanged || nameChanged || currencyChanged);
    }, 400);
    return () => clearTimeout(id);
  }, [previewData, configCopy, configName, baseName, currency, baseCurrency]);

  // Save handler
  const handleSave = async () => {
    const validObj = validateObjectValues(previewData, [
      "companyLogo",
      "discount",
    ]);
    if (!validObj.valid) {
      setAlert("Some Required Fields are Empty");
      return;
    }
    const data = await saveConfig(
      selectedConfig,
      configName,
      JSON.stringify(previewData),
      currency,
    );
    if (data.error) {
      setAlert("Couldn't Save, Error Occurred");
    } else {
      // sync base/server values after successful save
      setConfigCopy(previewData);
      setBaseName(configName);
      setBaseCurrency(currency);
      setUnsaved(false);
      fetchConfigsData();
      message.success("Saved Config Successfully");
    }
  };

  // Cancel handler (revert to last server state)
  const handleCancel = () => {
    setPreviewData(configCopy);
    setConfigName(baseName);
    setCurrency(baseCurrency);
    setUnsaved(false);
  };

  // Create-new helper
  const handleCreateNewConfig = async (config) => {
    if(availableConfigs.length > 24){
      message.error("Cannot create more than 25 configs")
      return
    }
    setLoader(true);
    try {
      const newConfig = await createNewConfig(config);
      const list = await loadConfigs(dispatch);
      setAvailableConfigs(list);
      setSelectedConfig(newConfig.id);
    } finally {
      setLoader(false);
    }
  };
  const handleConfirmCreate = async () => {
    const values = Object.values(createForm);
    if (
      values.indexOf("") > -1 ||
      values.indexOf(undefined) > -1 ||
      values.indexOf(null) > -1
    ) {
      setFormError("error");
      return;
    }
    const config = { ...createForm };
    config["invoiceTitle"] = "Tax Invoice";
    await handleCreateNewConfig(JSON.stringify(config));
    setModalOpen(false)
    setCreateForm(
      {
    companyName: "",
    companyAddress: "",
    serviceAddress: "",
    duePeriod: 30,
  }
    )
  };
  return (
    <>
      <Modal
        title="Create New Config"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleConfirmCreate}
        okText="Create"
      >
        {formError.length > 0 && (
          <Alert type="error" closable={true} onClose={()=>setFormError('')} showIcon={true}  message="Form fields cannot be empty" />
        )}

        <div className="flex flex-col gap-3">
          <div>
            <div className="mb-1 font-medium">Company Name</div>
            <Input
            id={"companyname"}
              placeholder="Company Name"
              value={createForm.companyName}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, companyName: e.target.value }))
              }
            />
          </div>

          <div>
            <div className="mb-1 font-medium">Company Address</div>
            <Input.TextArea
            id={"companyaddress"}
              placeholder="Company Address"
              rows={3}
              value={createForm.companyAddress}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, companyAddress: e.target.value }))
              }
            />
          </div>

          <div>
            <div className="mb-1 font-medium">Service Address</div>
            <Input.TextArea
            id={"serviceaddress"}
              placeholder="Service Address"
              rows={3}
              value={createForm.serviceAddress}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, serviceAddress: e.target.value }))
              }
            />
          </div>

          <div>
            <div className="mb-1 font-medium">Due Period</div>
            <Input
              placeholder="Due Period (e.g. 15 days)"
              value={createForm.duePeriod}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, duePeriod: e.target.value }))
              }
            />
          </div>
           <div>
            <div className="mb-1 font-medium">Currency</div>
            <Input
              placeholder="Currency Symbol"
              value={createForm.currency}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, currency: e.target.value }))
              }
            />
          </div>
        </div>
      </Modal>


      {alert.length > 0 && (
        <Alert
          className="mb-3"
          showIcon
          type="error"
          message={alert}
          closable={true}
          onClose={() => setAlert("")}
        />
      )}

      {availableConfigs.length === 0 ? (
        <CenteredDisplay
          timeout={7000}
          title="Get Started"
          description="Create new config and Start using builder!"
          actionText="Create Config"
          actionCallback={()=>setModalOpen(true)}
        />
      ) : selectedConfig ? (
        <Splitter style={{ height: "100vh" }}>
          {/* LEFT PANEL - Builder */}
          <Splitter.Panel
            defaultSize="35%"
            min="35%"
            max="40%"
            style={{ padding: 10, overflowY: "auto" }}
          >
            <Row gutter={[8, 8]}>
              <Col>
                <Select
                style={{minWidth:200}}
                  className="w-full"
                  value={selectedConfig}
                  options={availableConfigs.map((item) => ({
                    key: item.id,
                    label: item.configName,
                    value: item.id,
                  }))}
                  onChange={(val) => setSelectedConfig(val)}
                />
              </Col>

              <Col>
                <ActionsMenu
                  configs={[
                    {
                      label: (
                        <span className="flex items-center gap-2">
                          <FileAddOutlined /> Create New Config
                        </span>
                      ),
                      callback: () => setModalOpen(true),
                    },
                    {
                      label: (
                        <span className="flex items-center gap-2 text-red-500">
                          <DeleteOutlined /> Delete Config
                        </span>
                      ),
                      callback: async () => {
                        await deleteConfig(selectedConfig);
                        window.location.reload()


                      },
                      danger: true,
                    },
                    {
                      label: (
                        <span className="flex items-center gap-2">
                          <PushpinOutlined /> Set as Default
                        </span>
                      ),
                      callback: async() => {const data = await setAsDefault(selectedConfig);dispatch(setDefaultInvoiceConfig(data.defaultInvoiceConfig))},
                    },
                    {
                      label: (
                        <span className="flex items-center gap-2">
                          <FilePdfOutlined /> Sample PDF
                        </span>
                      ),
                      callback: () =>
                        downloadSample(
                          preprocessInvoice(previewData, currency),
                          currency,
                        ),
                    },
                  ]}
                  title={
                    <Button className="ml-1">
                      <SettingOutlined />
                    </Button>
                  }
                />
              </Col>

              <Col>
                <Button className="ml-1" type="primary" onClick={handleSave}>
                  Save
                </Button>
              </Col>
            </Row>

            <Row>
              {unsaved && (
                <Alert
                  className="w-full mt-2"
                  type="warning"
                  showIcon
                  message="Config has Unsaved Changes. Save to avoid data being lost."
                  action={
                    <Button onClick={handleCancel} size="small">
                      Cancel Changes
                    </Button>
                  }
                />
              )}
            </Row>

            <Row align="middle" gutter={[8, 0]} className="mt-2">
              <Col>
                <Typography.Title
                  className="p-2"
                  editable={{
                    onChange: (val) => {
                      setConfigName(val);
                    },
                  }}
                  level={5}
                >
                  {configName}
                </Typography.Title>
              </Col>

              <Col>
                <Typography.Title
                  className="p-2 w-15"
                  editable={{
                    maxLength: 5,
                    onChange: (val) => setCurrency(val),
                  }}
                  level={5}
                >
                  {currency}
                </Typography.Title>
              </Col>
            </Row>

            <BuilderTools
              config={undefined}
              key={ "invoicebuilder"}
              loadedConfig={ loadedConfig}
              setPreviewData={setPreviewData}
            />
          </Splitter.Panel>

          {/* RIGHT PANEL - Preview */}
          <Splitter.Panel
            defaultSize="65%"
            min={300}
            style={{ padding: 10, overflow: "auto" }}
          >
            <TemplatePreview previewData={previewData} currency={currency} />
          </Splitter.Panel>
        </Splitter>
      ) : null}
    </>
  );
};

export default InvoiceBuilder;
